import { call, put } from 'redux-saga/effects';
import moment from 'moment';
import { ApiError, ApiResponse } from 'store/api/types';
import { logoutRequest } from 'store/auth/actions';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { TaxReportCountry, VatTaxReportType } from 'store/app/enums';
import { checkResponse } from 'store/utils';
import { push } from 'store/navigation/actions';
import routes from 'routes';
import { buildGroupReturnContextSearch } from 'utils/group-return-context';
import { renderErrorMessage } from 'utils/render-error-message';
import { fetchRunDataSuccess } from 'store/vat/actions';
import { AdjustStatus, VatRunData } from 'store/vat/models/vatRunData';
import { previewTifdReport } from '../api';
import {
  previewTifdReportFailure,
  previewTifdReportSuccess,
} from '../actions';
import { TifdReport } from '../models';
import { PreviewTifdReportRequestAction } from '../types';

const GENERATED_DATE_FORMAT = 'DD/MM/YYYY HH:mm:ss';
const PERIOD_DATE_FORMAT = 'DD/MM/YYYY';

const readReport = (response: ApiResponse): TifdReport | null => {
  const data = response?.data;
  if (data && typeof data === 'object' && 'report' in data && data.report) {
    return data.report as TifdReport;
  }
  if (data && typeof data === 'object' && Array.isArray(data.sheets)) {
    return data as unknown as TifdReport;
  }
  return null;
};

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* previewTifdReportSaga(action: PreviewTifdReportRequestAction) {
  try {
    const {
      applicationConnectionId,
      periodStart,
      periodEnd,
      roundingCode,
      connectionId,
      mappingId,
      currency,
      providerName,
      connectionName,
      reportingTypeId,
      taxReportTypeName,
      jsonMapping,
      shortCode,
      groupContext,
    } = action.payload;

    if (!applicationConnectionId) {
      const message: MessageData = {
        title: 'TIFD preview',
        description: 'This entity has no organisation id to preview with.',
        type: MessageStates.ERROR,
      };
      yield put(turnMessageOn(message));
      yield put(previewTifdReportFailure());
      return;
    }

    const response: ApiResponse = yield call(
      previewTifdReport,
      applicationConnectionId,
      { periodStart, periodEnd, roundingCode }
    );

    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const report = readReport(response);
    if (!report) {
      const message: MessageData = {
        title: 'TIFD preview',
        description: 'The preview response did not include a report.',
        type: MessageStates.ERROR,
      };
      yield put(turnMessageOn(message));
      yield put(previewTifdReportFailure());
      return;
    }

    const fromLabel = moment(report.periodStart || periodStart).format(
      PERIOD_DATE_FORMAT
    );
    const toLabel = moment(report.periodEnd || periodEnd).format(
      PERIOD_DATE_FORMAT
    );
    const generatedDate = report.generatedDate
      ? moment.utc(report.generatedDate).format(GENERATED_DATE_FORMAT)
      : moment.utc().format(GENERATED_DATE_FORMAT);

    const vatRunRequestData = {
      connectionId,
      mappingId,
      currency: report.currency || currency,
      fromDate: report.periodStart || periodStart,
      toDate: report.periodEnd || periodEnd,
      provider: providerName ?? '',
    };
    const vatRunData: VatRunData = {
      adjustStatus: AdjustStatus.ADJUSTED_TOTALS,
      mappingId,
      tokenId: connectionId,
      fromDate: report.periodStart || periodStart,
      toDate: report.periodEnd || periodEnd,
      currency: report.currency || currency,
      jsonMapping: jsonMapping ?? '',
      openDataLogId: '',
      dataLogId: report.id ?? '',
      generationPeriod: `${fromLabel} - ${toLabel}`,
      generatedDate,
      validRange: true,
      taxReportCountry: TaxReportCountry.MALTA,
      isPeriodClosed: true,
      reportingTypeId: report.reportTypeId || reportingTypeId || '',
      taxReportTypeName: report.catalogueName || taxReportTypeName || '',
      isFirstTime: true,
      connectionName: connectionName ?? '',
      savable: true,
      taxType: VatTaxReportType.VAT_RETURN,
      shortCode: shortCode ?? '',
      status: report.status ?? 'NOT_SAVED',
      client: '',
      clientId: '',
      providerName: providerName ?? report.sourceType ?? '',
      taxRates: [],
      totalsSaved: false,
      updateFromProvider: false,
    };

    sessionStorage.setItem(
      'last-created-report',
      JSON.stringify(vatRunRequestData)
    );
    yield put(
      previewTifdReportSuccess(report, {
        connectionId,
        mappingId,
        applicationConnectionId,
      })
    );
    yield put(fetchRunDataSuccess(vatRunData));
    const search = buildGroupReturnContextSearch(groupContext);
    yield put(push(`${routes.vat.tifdReport}${search}`));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(previewTifdReportFailure());
  }
}
